// ReviewsSentimentFrame — reputation panel: average rating + total review
// count KPI, an optional positive/neutral/negative sentiment donut, and an
// optional per-source horizontal bar (Google, Yelp, Facebook, ...).
//
// Self-contained: only depends on recharts and the app's named theme tokens
// (no hardcoded hex for layout; the sentiment donut uses fixed Core-palette
// hexes for the same reason CallOutcomesDonut.tsx does — sentiment color
// identity must read the same in light and dark mode, unlike
// `--color-coral`/`--color-teal` which intentionally swap value per theme).

import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export interface ReviewsSentimentFrameProps {
  total_reviews: number;
  average_rating?: number;
  sentiment?: {
    positive: number;
    neutral: number;
    negative: number;
  };
  by_source?: Array<{ label: string; value: number }>;
}

// Sample data for standalone preview / storybook-style rendering.
export const demoReviews: ReviewsSentimentFrameProps = {
  total_reviews: 214,
  average_rating: 4.6,
  sentiment: { positive: 168, neutral: 31, negative: 15 },
  by_source: [
    { label: "Google", value: 132 },
    { label: "Facebook", value: 44 },
    { label: "Yelp", value: 26 },
    { label: "BBB", value: 12 },
  ],
};

// Fixed hexes (not theme CSS vars): positive/negative sentiment must read
// the same in light and dark mode, same rationale as CallOutcomesDonut.tsx.
const SENTIMENT_COLORS = {
  positive: "#0A76A8", // azure
  neutral: "#6B7280", // grey
  negative: "hsl(var(--status-error))",
} as const;

const CHART_HEIGHT = 180;

function StarRating({ value }: { value: number }) {
  const clamped = Math.max(0, Math.min(5, value));
  const rounded = Math.round(clamped * 2) / 2; // nearest half-star
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${clamped.toFixed(1)} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => {
        const fill = Math.max(0, Math.min(1, rounded - i));
        return (
          <span key={i} className="relative inline-block leading-none" style={{ fontSize: 16 }}>
            <span className="text-muted-foreground/40">★</span>
            {fill > 0 && (
              <span
                className="absolute inset-0 overflow-hidden text-coral"
                style={{ width: `${fill * 100}%` }}
              >
                ★
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

export function ReviewsSentimentFrame({
  total_reviews,
  average_rating,
  sentiment,
  by_source,
}: ReviewsSentimentFrameProps) {
  const sentimentSlices = sentiment
    ? (
        [
          { key: "positive", name: "Positive", value: sentiment.positive, color: SENTIMENT_COLORS.positive },
          { key: "neutral", name: "Neutral", value: sentiment.neutral, color: SENTIMENT_COLORS.neutral },
          { key: "negative", name: "Negative", value: sentiment.negative, color: SENTIMENT_COLORS.negative },
        ] as const
      ).filter((s) => s.value > 0)
    : [];
  const sentimentTotal = sentimentSlices.reduce((sum, s) => sum + s.value, 0);
  const hasSentiment = sentimentTotal > 0;
  const hasSources = !!by_source && by_source.length > 0;
  const hasAnyBody = hasSentiment || hasSources;

  if (total_reviews <= 0 && !average_rating && !hasAnyBody) {
    return (
      <div
        className="flex items-center justify-center text-dash-context text-muted-foreground"
        style={{ height: CHART_HEIGHT }}
      >
        No review data yet
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4 rounded-lg border border-border bg-card px-4 py-3">
        <div className="flex flex-col gap-1">
          <span className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
            Average rating
          </span>
          <div className="flex items-baseline gap-2">
            <span className="tabular text-dash-value font-bold text-foreground">
              {average_rating != null ? average_rating.toFixed(1) : "—"}
            </span>
            {average_rating != null && <StarRating value={average_rating} />}
          </div>
        </div>
        <div className="ml-auto flex flex-col items-end gap-1">
          <span className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
            Total reviews
          </span>
          <span className="tabular text-dash-value font-bold text-foreground">
            {total_reviews.toLocaleString()}
          </span>
        </div>
      </div>

      {hasAnyBody && (
        <div className="flex flex-col gap-4 sm:flex-row">
          {hasSentiment && (
            <div className="flex flex-1 items-center gap-4" style={{ height: CHART_HEIGHT }}>
              <div className="relative h-full w-[130px] shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sentimentSlices}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={62}
                      paddingAngle={2}
                      stroke="#FFFFFF"
                      strokeWidth={2}
                      isAnimationActive={false}
                    >
                      {sentimentSlices.map((s) => (
                        <Cell key={s.key} fill={s.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: "1px solid var(--color-border)",
                        fontSize: 12,
                      }}
                      formatter={(value: number, name) => [
                        `${value.toLocaleString()} (${Math.round((value / sentimentTotal) * 100)}%)`,
                        name,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="tabular text-dash-value font-semibold text-foreground">
                    {sentimentTotal.toLocaleString()}
                  </span>
                  <span className="text-dash-context text-muted-foreground">rated</span>
                </div>
              </div>
              <ul className="min-w-0 flex-1 space-y-1.5">
                {sentimentSlices.map((s) => (
                  <li key={s.key} className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: s.color }} />
                    <span className="truncate text-dash-context text-foreground">{s.name}</span>
                    <span className="tabular ml-auto shrink-0 text-dash-context text-muted-foreground">
                      {s.value.toLocaleString()} ·{" "}
                      {sentimentTotal > 0 ? Math.round((s.value / sentimentTotal) * 100) : 0}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* min-w-0 below to match this row's other column (the sources list
              above): a ResponsiveContainer measures its parent, and without
              the override that parent keeps flexbox's content-based
              `min-width: auto` floor and won't shrink to its share. */}
          {hasSources && (
            <div className="flex-1 min-w-0" style={{ height: CHART_HEIGHT }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={by_source}
                  layout="vertical"
                  margin={{ top: 4, right: 16, bottom: 0, left: 4 }}
                >
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "var(--color-muted-foreground)" }} />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={80}
                    tick={{ fontSize: 12, fill: "var(--color-muted-foreground)" }}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "1px solid var(--color-border)",
                      fontSize: 12,
                    }}
                    formatter={(value: number) => [value.toLocaleString(), "Reviews"]}
                  />
                  <Bar dataKey="value" fill="var(--color-coral)" radius={[0, 4, 4, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
